<template>
  <div
    :class="['zg-label', {
      'zg-label--round': isRound,
      'zg-label--no-margin': noMargin,
      [`zg-label--size-${size}`]: size,
      [`zg-label--background-${background}`]: background,
      'zg-label--no-border-radius': noBorderRadius
    }]"
  >
    <zg-icon
      v-if="icons && icons.left"
      :icon="icons.left"
      :rotate="icons.leftRotation"
      :size="iconSize[size]"
    />

    <span
      v-if="text"
      :class="{ 'text-black': textColor === 'black', 'text-white': textColor === 'white'}"
    >
      {{ text }}
    </span>

    <zg-icon
      v-if="icons && icons.right"
      :icon="icons.right"
      :rotate="icons.rightRotation"
      :size="iconSize[size]"
    />

    <zg-tooltip
      v-if="tooltip && size !== 'small'"
      v-bind="tooltip"
      black-trigger
    />
  </div>
</template>

<script>
  import ZgIcon from '../ZgIcon/ZgIcon.vue'
  import ZgTooltip from '../ZgTooltip/ZgTooltip'

  export default {
    name: 'ZgLabel',
    components: { ZgTooltip, ZgIcon },
    props: {
      text: {
        type: String,
        required: false,
        default: null
      },
      textColor: {
        type: String,
        required: false,
        default: 'black',
        validator: value => [
          'black',
          'white'
        ].includes(value)
      },
      size: {
        type: String,
        required: false,
        default: 'medium',
        validator: value => [
          'small',
          'medium',
          'large'
        ].includes(value)
      },
      icons: {
        type: Object,
        required: false,
        default: () => ({
          left: null,
          leftRotation: 0,
          right: null,
          rightRotation: 0
        })
      },
      background: {
        type: String,
        required: false,
        default: 'secondary-500',
        validator: value => [
          'secondary-500',
          'warning-500',
          'success-500',
          'success-300',
          'brown',
          'brand-300',
          'accent-500',
          'accent-300'
        ].includes(value)
      },
      tooltip: {
        type: Object,
        required: false,
        default: null
      },
      noMargin: {
        type: Boolean,
        required: false,
        default: false
      },
      noBorderRadius: {
        type: Boolean,
        required: false,
        default: false
      }
    },
    computed: {
      iconSize () {
        return {
          small: 'xxsmall',
          medium: 'xsmall',
          large: 'small'
        }
      },
      isRound () {
        const onlyOneIcon = (this.icons.right && !this.icons.left) || (!this.icons.right && this.icons.left)
        const noIcons = !this.icons.right && !this.icons.left
        return (onlyOneIcon && !this.text) || (noIcons && this.text.length === 1)
      }
    }
  }
</script>

<style lang="scss" scoped>
  .zg-label {
    display: inline-flex;
    align-items: center;
    border-radius: rem(99px);

    &--round {
      justify-content: center;
      padding: 0;
    }

    &--round.zg-label--small {
      width: rem(24px);
    }

    &--round.zg-label--medium {
      width: rem(32px);
    }

    &--round.zg-label--large {
      width: rem(40px);
    }
  }

  // ? ----------------------------------------------------------------------
  // text colors
  // ? ----------------------------------------------------------------------

  .text {
    &-white {
      color: color(white)
    }

    &-black {
      color: color(black)
    }
  }

  // ? ----------------------------------------------------------------------
  // size
  // ? ----------------------------------------------------------------------

  .zg-label--size-small {
    @include type-title-xxxs;

    height: rem(24px);
    gap: rem(3.5px);
    margin: 0;
    padding: 0 rem(13.5px);
  }

  .zg-label--size-medium {
    @include type-title-xxs;

    height: rem(32px);
    gap: rem(3.5px);
    margin: 0;
    padding: 0 rem(17.5px);
  }

  .zg-label--size-large {
    @include type-title-xs;

    height: rem(40px);
    gap: rem(5.75px);
    margin: 0;
    padding: 0 rem(21.75px);

    ::v-deep svg {
      width: 20px;
      height: 20px;
    }
  }

  // ? ----------------------------------------------------------------------
  // round
  // ? ----------------------------------------------------------------------

  .zg-label--round {
    justify-content: center;
    padding: 0;

    &.zg-label--size-small {
      width: rem(24px);
    }

    &.zg-label--size-medium {
      width: rem(32px);
    }

    &.zg-label--size-large {
      width: rem(40px);
    }
  }

  // ? ----------------------------------------------------------------------
  // background
  // ? ----------------------------------------------------------------------

  .zg-label--background-secondary-500 {
    background-color: color(secondary-500);
  }

  .zg-label--background-warning-500 {
    background-color: color(warning-500);
  }

  .zg-label--background-success-300 {
    background-color: color(success-300);
  }

  .zg-label--background-brown {
    background-color: color(brown);
  }

  .zg-label--background-brand-300 {
    background-color: color(brand-300);
  }

  .zg-label--background-accent-500 {
    background-color: color(accent-500);
  }

  .zg-label--background-accent-300 {
    background-color: color(accent-300);
  }

  // ? ----------------------------------------------------------------------
  // modifiers
  // ? ----------------------------------------------------------------------

  .zg-label--no-margin {
    margin: 0;
  }
  .zg-label--no-border-radius {
    border-radius: unset;
  }
</style>
